import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function DuprProfile({ api }) {
  const [config, setConfig] = useState(null);
  const [profile, setProfile] = useState(null);
  const [login, setLogin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const frame = useRef(null);
  const accepting = useRef(false);
  const load = useCallback(async () => {
    const c = (await api.get('/dupr/config')).data;
    setConfig(c);
    if (c.enabled && c.configured) setProfile((await api.get('/dupr/me')).data);
  }, [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (!login) return undefined;
    const handler = async (event) => {
      if (event.origin !== login.origin || event.source !== frame.current?.contentWindow || accepting.current) return;
      let value = event.data;
      if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return; } }
      const accessToken = value?.accessToken || value?.userToken;
      if (!accessToken || !value?.refreshToken) return;
      accepting.current = true; setBusy(true); setError('');
      try {
        await api.post('/dupr/connect', { state: login.state, accessToken, refreshToken: value.refreshToken });
        setLogin(null); await load();
      } catch (e) { setError(e.response?.data?.message || 'Could not verify DUPR login.'); }
      finally { accepting.current = false; setBusy(false); }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [api, login, load]);
  async function action(fn) {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.response?.data?.message || 'DUPR is temporarily unavailable. Please try again.'); }
    finally { setBusy(false); }
  }
  if (!config?.enabled) return null;
  return <section className="rounded-2xl border bg-white p-5 my-4" aria-label="DUPR account">
    <h2 className="text-lg font-bold">DUPR rating</h2>
    {!config.configured ? <p>Account linking is being set up.</p> : <>
      <p className="text-sm my-2">{profile?.status === 'Connected' ? `Connected · ${profile.duprId}` : profile?.status === 'ReconnectRequired' ? 'Reconnect your DUPR account' : 'Connect your account to join rated sessions.'}</p>
      {profile?.connected && <><div className="flex gap-8 my-4"><div>Singles <strong>{profile.singles ?? 'Unrated'}</strong></div><div>Doubles <strong>{profile.doubles ?? 'Unrated'}</strong></div></div><p className="text-xs">Last synced: {profile.lastSyncedUtc ? new Date(profile.lastSyncedUtc).toLocaleString() : 'Waiting for rating sync'}</p></>}
      <div className="flex flex-wrap gap-3 mt-4">
        <button disabled={busy} className="rounded-xl bg-[#264D59] px-4 py-2 text-white font-semibold disabled:opacity-50" onClick={() => action(async () => setLogin((await api.post('/dupr/connect/start')).data))}>{profile?.connected ? 'Reconnect DUPR' : 'Connect DUPR'}</button>
        {profile?.connected && <button disabled={busy} className="rounded-xl border border-[#CBD5E1] px-4 py-2 font-semibold disabled:opacity-50" onClick={() => action(async () => { await api.post('/dupr/refresh'); await load(); })}>Refresh DUPR data</button>}
        {profile?.status && profile.status !== 'NotConnected' && profile.status !== 'Disconnected' && <button disabled={busy} className="rounded-xl border border-[#CBD5E1] px-4 py-2 font-semibold disabled:opacity-50" onClick={() => action(async () => { await api.post('/dupr/disconnect'); await load(); })}>Disconnect</button>}
      </div>
    </>}
    {error && <p role="alert" className="text-red-700 mt-3">{error}</p>}
    {login && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Connect DUPR"><div className="bg-white rounded-2xl p-4 w-full max-w-xl"><button className="mb-3" disabled={busy} onClick={() => setLogin(null)}>Close DUPR login</button><iframe ref={frame} title="DUPR login" src={login.url} allow="payment" className="w-full h-[70vh] border-0" /></div></div>}
    {config.supportEmail && <a className="block text-sm underline mt-4" href={`mailto:${config.supportEmail}`}>Contact FREAC support</a>}
  </section>;
}
